import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { TutorialCard } from '@/components/TutorialCard';
import { useAppData } from '@/hooks/useAppData';
import { useUserActions } from '@/hooks/useUserActions';
import { Search, Home as HomeIcon, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';


export default function SearchResults() {
  const location = useLocation();
  const { tutorials } = useAppData();
  const { favorites, toggleFavorite, addHistory } = useUserActions();
  
  const query = new URLSearchParams(location.search).get('q') || '';

  const results = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return tutorials.filter((t) => 
      t.name.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.tag.toLowerCase().includes(q) ||
      t.difficulty.toLowerCase().includes(q)
    );
  }, [tutorials, query]);

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-8">
          <Link to="/" className="flex items-center gap-1 hover:text-primary transition-colors">
            <HomeIcon className="h-3.5 w-3.5" /> 首页
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground font-medium">搜索结果</span>
        </nav>

        {/* Header */}
        <div className="mb-12">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-primary font-bold">
              <Search className="h-5 w-5" />
              <span>搜索结果</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              关于 “{query}” 的搜索结果
            </h1>
            <p className="text-muted-foreground mt-2">
              共找到 {results.length} 条记录
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {results.map((tutorial) => (
            <TutorialCard
              key={tutorial.id}
              tutorial={tutorial}
              isFavorited={favorites.includes(tutorial.id)}
              onFavoriteToggle={toggleFavorite}
              onClick={addHistory}
            />
          ))}
        </div>

        {results.length === 0 && (
          <div className="py-24 text-center border rounded-2xl bg-muted/20">
            <div className="max-w-md mx-auto">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
              <h3 className="text-lg font-bold mb-2">暂无相关内容</h3>
              <p className="text-muted-foreground mb-6">试试其他关键词，或者浏览首页分类</p>
              <Button variant="outline" asChild>
                <Link to="/">回到首页</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
